/**
 * Streaming check + summarize over SSE-via-fetch.
 *
 * Mirrors `jackhammer-ui/src/hooks/useStreamingCheck.js` but typed against
 * `@eloquor/api-contract`. The transport is plain `fetch().body.getReader()`
 * (not EventSource) so we can POST a JSON body AND attach cookies.
 */

import type {
  CheckRequest,
  CheckSseEvent,
  SummarizeRequest,
  SummarizeSseEvent,
} from '@eloquor/api-contract';
import { apiFetchRaw } from './client';

export async function* streamCheck(
  fileId: string,
  req: CheckRequest = {},
  signal?: AbortSignal
): AsyncGenerator<CheckSseEvent> {
  const res = await apiFetchRaw(`/files/${encodeURIComponent(fileId)}/check`, {
    method: 'POST',
    json: req,
    signal,
  });
  for await (const event of readSse<CheckSseEvent>(res, signal)) yield event;
}

export async function* streamSummarize(
  fileId: string,
  req: SummarizeRequest = {},
  signal?: AbortSignal
): AsyncGenerator<SummarizeSseEvent> {
  const res = await apiFetchRaw(`/files/${encodeURIComponent(fileId)}/summarize`, {
    method: 'POST',
    json: req,
    signal,
  });
  for await (const event of readSse<SummarizeSseEvent>(res, signal)) yield event;
}

/**
 * Reads `data: <json>\n\n` chunks until the stream ends. Buffers across
 * chunk boundaries. Malformed JSON chunks are skipped, not surfaced.
 */
async function* readSse<T>(res: Response, signal?: AbortSignal): AsyncGenerator<T> {
  const body = res.body;
  if (!body) return;
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      if (signal?.aborted) return;
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseSseChunk<T>(raw);
        if (parsed) yield parsed;
      }
    }
  } finally {
    reader.releaseLock?.();
  }
}

function parseSseChunk<T>(raw: string): T | null {
  // Each chunk is one or more `field: value` lines; we only care about `data:`.
  const lines = raw.split('\n');
  for (const line of lines) {
    if (!line.startsWith('data:')) continue;
    const json = line.slice(5).trimStart();
    if (!json) continue;
    try {
      return JSON.parse(json) as T;
    } catch {
      return null;
    }
  }
  return null;
}
